import { checklistValuesToDraft } from '@/services/checklist/checklistObservations';
import { Platform } from 'react-native';
import { databaseService } from '../databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { showToast } from '../../utils/toast';
import { getLogger } from '../../utils/logger';

// Logger específico para o Helper
const logger = getLogger('CHECKLIST-ACTIONS-HELPER');

type SettledFulfilled<T> = { status: 'fulfilled'; value: T };
type SettledRejected = { status: 'rejected'; reason: unknown };
type SettledResult<T> = SettledFulfilled<T> | SettledRejected;

const settleAll = async <T>(promises: Array<Promise<T>>): Promise<SettledResult<T>[]> => {
  if (typeof Promise.allSettled === 'function') {
    const settled = await Promise.allSettled(promises);
    return settled as SettledResult<T>[];
  }

  return Promise.all(
    promises.map(async (promise) => {
      try {
        const value = await promise;
        return { status: 'fulfilled', value } as SettledFulfilled<T>;
      } catch (reason) {
        return { status: 'rejected', reason } as SettledRejected;
      }
    })
  );
};

/**
 * Opções genéricas para ações de checklist.
 */
export interface ChecklistActionOptions {
  /**
   * Pular o registro de operação pendente (útil para migrações ou testes).
   * Por padrão é FALSE, garantindo a criação da pendência.
   */
  skipPendingOp?: boolean;
  /**
   * Suprimir toasts de feedback. Default FALSE.
   */
  silent?: boolean;
}

/**
 * Opções adicionais para geração de PDF em lote.
 */
export interface GeneratePdfOptions extends ChecklistActionOptions {
  /** Callback de progresso – recebe (concluídos, total). */
  onProgress?: (done: number, total: number) => void;
  /** Nome do arquivo .zip resultante (web). */
  zipFileName?: string;
}

/**
 * Helper de ações de Checklist – centraliza concluir, deletar e gerar PDF.
 * Cada método é auto contido e seguro para uso em qualquer lugar do app
 * (Dashboard, tela individual, Sync, etc.).
 */
export const ChecklistActionsHelper = {
  /**
   * Marca um checklist como concluído.
   * 1. Faz bulk upsert dos valores pendentes (draftValues).
   * 2. Atualiza status para 'done' + data_conclusao.
   */
  async completeOne(
    checklistId: string,
    draftValues: Record<string, any>,
    opts: ChecklistActionOptions = {}
  ): Promise<void> {
    logger.info('[completeOne] Iniciando', { checklistId });
    // Carrega checklist para obter empresa_id
    const checklist: any = await databaseService.readItem(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
      checklistId
    );

    if (!checklist) {
      const msg = `Checklist não encontrado: ${checklistId}`;
      logger.error('[completeOne] ' + msg);
      throw new Error(msg);
    }

    // 1) Upsert de valores alterados
    const hasDraft = draftValues && Object.keys(draftValues).length > 0;
    if (hasDraft) {
      logger.info('[completeOne] bulkUpsertChecklistValues', {
        numCampos: Object.keys(draftValues).length,
      });
      await databaseService.bulkUpsertChecklistValues(
        checklist.empresa_id,
        checklist.id,
        draftValues,
        { skipPendingOp: !!opts.skipPendingOp }
      );
    }

    // 2) Atualizar status
    await databaseService.updateItem(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
      checklist.id,
      {
        status: 'done',
        data_conclusao: Date.now(),
        updated_at: Date.now(),
      },
      !!opts.skipPendingOp
    );

    if (!opts.silent) {
      showToast('Checklist concluído!', { duration: 1200, type: 'success' });
    }
  },

  /**
   * Deleção em lote de checklists. Mantém comportamento offline-first, ou seja,
   * cada deleção continua registrando pendência (skipPendingOp = false).
   * Filas e conteúdo anterior ficam preservados na mesma transação da exclusão lógica.
   */
  async deleteMany(ids: string[], opts: ChecklistActionOptions = {}): Promise<void> {
    if (!ids?.length) return;
    logger.info('[deleteMany] Iniciando', { total: ids.length });

    const chunkSize = 40; // compatível com SyncService
    let processed = 0;
    let successCount = 0;
    let failureCount = 0;
    let totalImagesRemoved = 0;

    for (let i = 0; i < ids.length; i += chunkSize) {
      const slice = ids.slice(i, i + chunkSize);
      
      // Cada checklist conserva evidência e grava sua exclusão lógica atomicamente.
      const settled = await settleAll(
        slice.map(async (id) => {
          if (opts.skipPendingOp) throw new Error('Exclusão de checklist exige outbox e retenção.');
          const deleteSuccess = await databaseService.deleteChecklistPreservingMedia(id);
          return { deleteSuccess, imagesRemoved: 0 };
        })
      );
      
      // Processar resultados do chunk
      settled.forEach((res) => {
        if (res.status === 'fulfilled') {
          if (res.value.deleteSuccess) {
            successCount += 1;
            totalImagesRemoved += res.value.imagesRemoved;
          } else {
            failureCount += 1;
          }
        } else {
          failureCount += 1;
        }
      });
      
      processed += slice.length;
      logger.info('[deleteMany] Chunk concluído', {
        processed,
        total: ids.length,
        successCount,
        failureCount,
        totalImagesRemoved,
      });
    }

    if (!opts.silent) {
      const msg = `${successCount} de ${ids.length} checklists deletados` +
        (failureCount ? ` (${failureCount} falharam)` : '') +
        (totalImagesRemoved > 0 ? ` • ${totalImagesRemoved} imagens removidas` : '');
      showToast(msg, {
        duration: 3000, // Aumentado para dar tempo de ler sobre as imagens
        type: failureCount ? 'warning' : 'info',
      });
    }
  },

  /**
   * Gera PDF para um ou mais checklists.
   * Retorna array com { id, uri } – em web o uri pode ser string vazia
   * pois o download é iniciado automaticamente.
   */
  async generatePdfMany(
    ids: string[],
    opts: GeneratePdfOptions = {}
  ): Promise<{ id: string; uri: string }[]> {
    console.log('🐛 DEBUG - generatePdfMany iniciado', { 
      platform: Platform.OS, 
      total: ids.length, 
      useZip: (Platform.OS !== 'web' && ids.length > 1) 
    });
    if (!ids?.length) return [];
    logger.info('[generatePdfMany] Iniciando', { total: ids.length });

    const isWeb = Platform.OS === 'web';
    const isMobile = Platform.OS !== 'web';
    // 2025: ZIP habilitado para múltiplos PDFs em ambas as plataformas
    //const useZip = (isWeb && !!opts.zipFileName) || (isMobile && ids.length > 1);
    const useZip = (isWeb && ids.length > 1) || (isMobile && ids.length > 1);

    // ===== Importações dinâmicas =====
    // Evita incluir libs pesadas no bundle quando não necessário.
    let generatePdfFn: any;
    let buildPdfDoc: any;
    let JSZip: any;
    let saveAs: any;
    let FileSystem: any;
    let Sharing: any;

    if (useZip) {
      // Para compactação, precisamos apenas do builder, não do service que dispara download individual
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      buildPdfDoc = require('../checklistPdfDoc').buildChecklistPdfDoc;
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      JSZip = require('jszip');
      
      if (isWeb) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        ({ saveAs } = require('file-saver'));
      } else {
        // Mobile: importar FileSystem e Sharing para salvar e compartilhar o ZIP
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        FileSystem = require('expo-file-system/legacy');
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        Sharing = require('expo-sharing');
      }
    } else {
      if (isWeb) {
        // Geração individual → usa serviço existente que já dispara download
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        generatePdfFn = require('../checklistPdfService.web').generateChecklistPdfWeb;
      } else {
        // Mobile: geração individual com sharing
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        generatePdfFn = require('../checklistPdfService.native').generateChecklistPdfNative;
      }
    }

    if (!generatePdfFn && !buildPdfDoc) {
      throw new Error('Função de geração de PDF não encontrada');
    }

    const results: { id: string; uri: string }[] = [];
    // Instância do zip quando necessário
    const zipInstance = useZip ? new JSZip() : null;
    let done = 0;

    // Processa em chunks menores para evitar travar UI (principalmente Web)
    const chunkSize = Platform.OS === 'web' ? 10 : 5;
    let failureCount = 0;
    for (let i = 0; i < ids.length; i += chunkSize) {
      const slice = ids.slice(i, i + chunkSize);
      // eslint-disable-next-line no-await-in-loop
      const settled = await settleAll(
        slice.map(async (id) => {
          // Carrega checklist + template + valores
          const checklist: any = await databaseService.readItem(
            APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
            id
          );
          if (!checklist) throw new Error('Checklist não encontrado: ' + id);

          const template = await databaseService.listItems<any>(
            APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
            { template_id: checklist.template_id }
          );
          template.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

          const valsArr = await databaseService.listItems<any>(
            APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
            { checklist_id: id }
          );
          const values = checklistValuesToDraft(valsArr);

          if (useZip) {
            const pdfDoc = await buildPdfDoc({ checklist, template, values });
            const pdfBytes: Uint8Array = await pdfDoc.save();
            zipInstance!.file(`checklist-${id}.pdf`, pdfBytes);
            return { id, uri: '' };
          }

          // Para geração individual, evitar sharing duplicado em mobile quando via dashboard
          const skipSharing = isMobile && ids.length > 1;
          const uri = await generatePdfFn({ 
            checklist, 
            template, 
            values,
            ...(isMobile && { skipSharing }) // só passa skipSharing no mobile
          });
          return { id, uri: uri ?? '' };
        })
      );

      settled.forEach((res) => {
        if (res.status === 'fulfilled') {
          // @ts-ignore
          results.push(res.value);
        } else {
          failureCount += 1;
          logger.error('[generatePdfMany] Falha na geração de PDF', res.reason);
        }
      });

      done = results.length + failureCount;
      if (opts.onProgress) opts.onProgress(done, ids.length);
    }

    // Finaliza o .zip se necessário
    if (useZip && zipInstance) {
      const fileName = opts.zipFileName ?? `checklists_${ids.length}.zip`;
      
      if (isWeb) {
        // Web: download direto via file-saver
        const zipBlob: Blob = await zipInstance.generateAsync({ type: 'blob' });
        saveAs(zipBlob, fileName);
        logger.info('[generatePdfMany] ZIP web gerado e download iniciado', { fileName });
      } else {
        // Mobile: salvar no cache e compartilhar via expo-sharing
        const zipArrayBuffer = await zipInstance.generateAsync({ type: 'arraybuffer' });
        const base64Data = btoa(String.fromCharCode(...new Uint8Array(zipArrayBuffer)));
        const zipUri = `${FileSystem.cacheDirectory}${fileName}`;
        
        await FileSystem.writeAsStringAsync(zipUri, base64Data, {
          encoding: FileSystem.EncodingType.Base64,
        });
        
        logger.info('[generatePdfMany] ZIP mobile criado no cache', { 
          fileName, 
          uri: zipUri, 
          sizeMB: (zipArrayBuffer.byteLength / 1024 / 1024).toFixed(2) 
        });
        
        // Compartilhar o arquivo ZIP
        try {
          await Sharing.shareAsync(zipUri, { 
            UTI: 'com.pkware.zip-archive', 
            mimeType: 'application/zip' 
          });
          logger.info('[generatePdfMany] ZIP mobile compartilhado com sucesso');
          
          // Remover ZIP após compartilhamento
          setTimeout(() => {
            FileSystem.deleteAsync(zipUri, { idempotent: true }).catch(() => {});
            logger.info('[generatePdfMany] ZIP removido do cache', { uri: zipUri });
          }, 5000);
        } catch (err) {
          logger.error('[generatePdfMany] Erro ao compartilhar ZIP mobile', err);
        }
      }
    }

    if (!opts.silent) {
      const msg = failureCount
        ? `${results.length} PDFs gerados, ${failureCount} falharam`
        : 'PDFs gerados!';
      showToast(msg, {
        duration: 2500,
        type: failureCount ? 'warning' : 'success',
      });
    }

    return results;
  },
};

export default ChecklistActionsHelper; 
